/**
 * The 91 view filter types.
 *
 * A static map rather than a runtime registry: nothing extends this set at
 * runtime, and being static buys exhaustiveness. `satisfies Record<
 * ViewFilterTypeName, ...>` means a filter type added on the backend fails to
 * compile here until it is answered for, rather than going missing at runtime
 * on somebody's view.
 */
import type { ViewFilterTypeName } from '@/api/types'

import {
  DEPRECATED_FILTERS,
  FILTER_COMPATIBILITY,
  PRIVATE_ONLY_FILTERS,
  VALUELESS_FILTERS,
} from './compatibility'
import * as arrays from './arrays'
import {
  DATE_PLACEMENTS as P,
  arrayDateFilter,
  dateFilter,
  deprecatedDateFilter,
  matchesDateEqualsDayOfMonth,
} from './date-filters'
import * as relations from './relations'
import * as scalar from './scalar'
import type { DateOperator } from './date-filters'
import type { ViewFilterTypeDef } from './types'

/** The periods `date_is_within` will not accept. */
const WITHIN_REFUSES: readonly DateOperator[] = ['today']

export * from './types'
export * from './predicates'

/** Assembles one filter definition from its matcher and the extracted data. */
function define<N extends ViewFilterTypeName>(
  type: N,
  matches: ViewFilterTypeDef<N>['matches'],
  refusedDateOperators: readonly DateOperator[] = [],
): ViewFilterTypeDef<N> {
  const { fieldTypes, formulaTypes } = FILTER_COMPATIBILITY[type]
  return {
    type,
    matches,
    refusedDateOperators,
    compatibleFieldTypes: [
      ...fieldTypes,
      (field) =>
        formulaTypes.includes((field as { formula_type?: string }).formula_type ?? ''),
    ],
    isDeprecated: DEPRECATED_FILTERS.has(type),
    isAllowedInPublicViews: !PRIVATE_ONLY_FILTERS.has(type),
    hasEditableValue: !VALUELESS_FILTERS.has(type),
  }
}

export const VIEW_FILTER_TYPES = {
  equal: define('equal', scalar.matchesEqual),
  not_equal: define('not_equal', scalar.matchesNotEqual),
  contains: define('contains', scalar.matchesContains),
  contains_not: define('contains_not', scalar.matchesContainsNot),
  contains_word: define('contains_word', scalar.matchesContainsWord),
  doesnt_contain_word: define('doesnt_contain_word', scalar.matchesDoesntContainWord),
  length_is_lower_than: define('length_is_lower_than', scalar.matchesLengthIsLowerThan),
  higher_than: define(
    'higher_than',
    scalar.orderingFilter((a, b) => a > b),
  ),
  higher_than_or_equal: define(
    'higher_than_or_equal',
    scalar.orderingFilter((a, b) => a >= b),
  ),
  lower_than: define(
    'lower_than',
    scalar.orderingFilter((a, b) => a < b),
  ),
  lower_than_or_equal: define(
    'lower_than_or_equal',
    scalar.orderingFilter((a, b) => a <= b),
  ),
  is_even_and_whole: define('is_even_and_whole', scalar.matchesIsEvenAndWhole),
  boolean: define('boolean', scalar.matchesBoolean),
  empty: define('empty', scalar.matchesEmpty),
  not_empty: define('not_empty', scalar.matchesNotEmpty),
  filename_contains: define('filename_contains', scalar.matchesFilenameContains),
  has_file_type: define('has_file_type', scalar.matchesHasFileType),
  files_lower_than: define('files_lower_than', scalar.matchesFilesLowerThan),

  single_select_equal: define('single_select_equal', relations.matchesSingleSelectEqual),
  single_select_not_equal: define(
    'single_select_not_equal',
    relations.matchesSingleSelectNotEqual,
  ),
  single_select_is_any_of: define(
    'single_select_is_any_of',
    relations.matchesSingleSelectIsAnyOf,
  ),
  single_select_is_none_of: define(
    'single_select_is_none_of',
    relations.matchesSingleSelectIsNoneOf,
  ),
  multiple_select_has: define('multiple_select_has', relations.matchesMultipleSelectHas),
  multiple_select_has_not: define(
    'multiple_select_has_not',
    relations.matchesMultipleSelectHasNot,
  ),
  multiple_collaborators_has: define(
    'multiple_collaborators_has',
    relations.matchesCollaboratorsHas,
  ),
  multiple_collaborators_has_not: define(
    'multiple_collaborators_has_not',
    relations.matchesCollaboratorsHasNot,
  ),
  user_is: define('user_is', relations.matchesUserIs),
  user_is_not: define('user_is_not', relations.matchesUserIsNot),
  link_row_has: define('link_row_has', relations.matchesLinkRowHas),
  link_row_has_not: define('link_row_has_not', relations.matchesLinkRowHasNot),
  link_row_contains: define('link_row_contains', relations.matchesLinkRowContains),
  link_row_not_contains: define(
    'link_row_not_contains',
    relations.matchesLinkRowNotContains,
  ),
  has_empty_value: define('has_empty_value', arrays.matchesHasEmptyValue),
  has_not_empty_value: define('has_not_empty_value', arrays.matchesHasNotEmptyValue),
  has_value_equal: define('has_value_equal', arrays.matchesHasValueEqual),
  has_not_value_equal: define('has_not_value_equal', arrays.matchesHasNotValueEqual),
  has_all_values_equal: define('has_all_values_equal', arrays.matchesHasAllValuesEqual),
  has_value_contains: define('has_value_contains', arrays.matchesHasValueContains),
  has_not_value_contains: define(
    'has_not_value_contains',
    arrays.matchesHasNotValueContains,
  ),
  has_value_contains_word: define(
    'has_value_contains_word',
    arrays.matchesHasValueContainsWord,
  ),
  has_not_value_contains_word: define(
    'has_not_value_contains_word',
    arrays.matchesHasNotValueContainsWord,
  ),
  has_value_length_is_lower_than: define(
    'has_value_length_is_lower_than',
    arrays.matchesHasValueLengthIsLowerThan,
  ),
  has_any_select_option_equal: define(
    'has_any_select_option_equal',
    arrays.matchesHasAnySelectOptionEqual,
  ),
  has_none_select_option_equal: define(
    'has_none_select_option_equal',
    arrays.matchesHasNoneSelectOptionEqual,
  ),
  has_value_higher: define('has_value_higher', arrays.matchesHasValueHigher),
  has_not_value_higher: define('has_not_value_higher', arrays.matchesHasNotValueHigher),
  has_value_higher_or_equal: define(
    'has_value_higher_or_equal',
    arrays.matchesHasValueHigherOrEqual,
  ),
  has_not_value_higher_or_equal: define(
    'has_not_value_higher_or_equal',
    arrays.matchesHasNotValueHigherOrEqual,
  ),
  has_value_lower: define('has_value_lower', arrays.matchesHasValueLower),
  has_not_value_lower: define('has_not_value_lower', arrays.matchesHasNotValueLower),
  has_value_lower_or_equal: define(
    'has_value_lower_or_equal',
    arrays.matchesHasValueLowerOrEqual,
  ),
  has_not_value_lower_or_equal: define(
    'has_not_value_lower_or_equal',
    arrays.matchesHasNotValueLowerOrEqual,
  ),
  // The multi-step date filters. One period, named by the filter value, and
  // seven ways of placing the row's date against it.
  date_is: define('date_is', dateFilter(P.is)),
  date_is_not: define('date_is_not', dateFilter(P.isNot)),
  date_is_before: define('date_is_before', dateFilter(P.isBefore)),
  date_is_on_or_before: define('date_is_on_or_before', dateFilter(P.isOnOrBefore)),
  date_is_after: define('date_is_after', dateFilter(P.isAfter)),
  date_is_on_or_after: define('date_is_on_or_after', dateFilter(P.isOnOrAfter)),
  // "Within today" would be an empty span, so the filter refuses the operator
  // outright rather than matching nothing.
  // The one filter that refuses a period: "within today" names an empty span.
  // The list is named once and passed to both halves, so the matcher and the
  // editor cannot disagree about it.
  date_is_within: define(
    'date_is_within',
    dateFilter(P.isWithin, WITHIN_REFUSES),
    WITHIN_REFUSES,
  ),

  // The same, over a lookup of dates.
  has_date_equal: define('has_date_equal', arrayDateFilter(P.is)),
  has_not_date_equal: define('has_not_date_equal', arrayDateFilter(P.is, true)),
  has_date_before: define('has_date_before', arrayDateFilter(P.isBefore)),
  has_not_date_before: define('has_not_date_before', arrayDateFilter(P.isBefore, true)),
  has_date_on_or_before: define('has_date_on_or_before', arrayDateFilter(P.isOnOrBefore)),
  has_not_date_on_or_before: define(
    'has_not_date_on_or_before',
    arrayDateFilter(P.isOnOrBefore, true),
  ),
  has_date_after: define('has_date_after', arrayDateFilter(P.isAfter)),
  has_not_date_after: define('has_not_date_after', arrayDateFilter(P.isAfter, true)),
  has_date_on_or_after: define('has_date_on_or_after', arrayDateFilter(P.isOnOrAfter)),
  has_not_date_on_or_after: define(
    'has_not_date_on_or_after',
    arrayDateFilter(P.isOnOrAfter, true),
  ),
  has_date_within: define('has_date_within', arrayDateFilter(P.isWithin)),
  has_not_date_within: define(
    'has_not_date_within',
    arrayDateFilter(P.isWithinFromToday, true),
  ),

  // The nineteen deprecated single-step filters, each a multi-step filter with
  // its operator fixed -- exactly what their own migration functions say.
  date_equal: define(
    'date_equal',
    deprecatedDateFilter(P.is, 'exact_date', { onUnusable: 'compare' }),
  ),
  date_not_equal: define(
    'date_not_equal',
    deprecatedDateFilter(P.isNot, 'exact_date', { onUnusable: 'compare', nullRow: true }),
  ),
  date_before: define('date_before', deprecatedDateFilter(P.isBefore, 'exact_date')),
  date_before_or_equal: define(
    'date_before_or_equal',
    deprecatedDateFilter(P.isOnOrBefore, 'exact_date'),
  ),
  date_after: define('date_after', deprecatedDateFilter(P.isAfter, 'exact_date')),
  date_after_or_equal: define(
    'date_after_or_equal',
    deprecatedDateFilter(P.isOnOrAfter, 'exact_date'),
  ),
  date_after_days_ago: define(
    'date_after_days_ago',
    deprecatedDateFilter(P.isOnOrAfter, 'nr_days_ago', {
      onUnusable: 'reject',
      emptyAbstains: false,
    }),
  ),
  date_equals_today: define('date_equals_today', deprecatedDateFilter(P.is, 'today')),
  date_before_today: define(
    'date_before_today',
    deprecatedDateFilter(P.isBefore, 'today'),
  ),
  date_after_today: define('date_after_today', deprecatedDateFilter(P.isAfter, 'today')),
  date_equals_week: define('date_equals_week', deprecatedDateFilter(P.is, 'this_week')),
  date_equals_month: define(
    'date_equals_month',
    deprecatedDateFilter(P.is, 'this_month'),
  ),
  date_equals_year: define('date_equals_year', deprecatedDateFilter(P.is, 'this_year')),
  date_within_days: define(
    'date_within_days',
    deprecatedDateFilter(P.isWithin, 'nr_days_from_now'),
  ),
  date_within_weeks: define(
    'date_within_weeks',
    deprecatedDateFilter(P.isWithin, 'nr_weeks_from_now', { rejectZero: true }),
  ),
  date_within_months: define(
    'date_within_months',
    deprecatedDateFilter(P.isWithin, 'nr_months_from_now', { rejectZero: true }),
  ),
  date_equals_days_ago: define(
    'date_equals_days_ago',
    deprecatedDateFilter(P.is, 'nr_days_ago'),
  ),
  date_equals_months_ago: define(
    'date_equals_months_ago',
    deprecatedDateFilter(P.is, 'nr_months_ago'),
  ),
  date_equals_years_ago: define(
    'date_equals_years_ago',
    deprecatedDateFilter(P.is, 'nr_years_ago'),
  ),

  // The one date filter with no interval: a calendar number, matched in any month.
  date_equals_day_of_month: define(
    'date_equals_day_of_month',
    matchesDateEqualsDayOfMonth,
  ),
} satisfies Record<ViewFilterTypeName, ViewFilterTypeDef>
